import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { environment } from 'environments/environment';
import { ModuleFederationToolsModule } from '@angular-architects/module-federation-tools';
import { ActivatedRoute } from '@angular/router';
import { WellSchematicDiagramService } from 'app/modules/well-schematic-diagram/services/well-schematic-diagram.service';
import { WellSchematicDiagramState } from 'app/modules/well-schematic-diagram/state/well-schematic-diagram.state';
import {
    WellSchematic,
    WellSchematicCdft,
} from 'app/modules/well-schematic-diagram/model';
import { forkJoin } from 'rxjs';
import module from 'app/modules/well-schematic-diagram/well-schematic-diagram.module';
import { toFormData } from 'app/core/shared/helper/url.helper';
import { Location } from '@angular/common';

@Component({
    selector: 'app-wbs-edit',
    templateUrl: './edit.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [...module, ModuleFederationToolsModule],
    styleUrls: ['./edit.page.scss'],
})
export class EditPage implements OnInit {
    isApplication: boolean;
    applicationId: string;
    webComponent;
    wellStringId;
    schematic: WellSchematic;
    cdftSchematic: WellSchematicCdft[];
    constructor(
        private route: ActivatedRoute,
        private service: WellSchematicDiagramService,
        private state: WellSchematicDiagramState,
        private location: Location
    ) { }

    async ngOnInit() {
        this.isApplication =
            this.route.snapshot.paramMap.get('type') != 'configuration';
        this.applicationId = this.route.snapshot.paramMap.get('type');
        this.wellStringId = this.route.snapshot.paramMap.get('wellStringId');
        this.schematic = await this.getSchematic(this.wellStringId);
        this.initiateComponent();
    }

    getSchematic(wellStringId, force = false): Promise<WellSchematic> {
        return new Promise((resolve, reject) => {
            if (!this.state.isEmpty() && !force) {
                resolve(this.state.findByWellStringId(wellStringId));
            }

            this.service
                .getWellConfigList({ Search: { wellStringId } })
                .subscribe((response) => {
                    resolve(response.data.data[0]);
                });
        });
    }

    initiateComponent() {
        this.webComponent = {
            options: {
                type: 'script',
                remoteEntry: `${environment.mfeReactUrl}`,
                remoteName: 'react',
                exposedModule: './well-barrier',
                elementName: 'well-barrier',
            },
            props: {},
            events: {
                create: (e) => this.create(e.detail),
                getAllConfig: (e) => this.getAllConfig(e.detail),
                update: (e) => this.update(e.detail),
                delete: (e) => this.delete(e.detail),
                assign: (e) => this.assign(e.detail),
                status: (e) => this.status(e.detail),
                submit: (e) => this.submit(e.detail),
                getCdft: (e) => this.getCdft(e.detail),
                getConfigurationHistory: (e) =>
                    this.getConfigurationHistory(e.detail),
                previousPage: (e) => { this.location.back() }

            },
        };
    }

    status(event) {
        const payload = {
            ...event.payload,
            id: this.applicationId,
            remark: '',
            wellStringId: this.wellStringId,
        };

        const formData: any = toFormData(payload);

        this.service.saveApplicationStatus(formData).subscribe((response) => {
            event.resolve(response);
        });
    }

    create(event) {
        this.service.saveWellConfig(event.payload).subscribe((response) => {
            event.resolve(response);
        });
    }

    update(event) {
        this.service.updateWellConfig(event.payload).subscribe((response) => {
            event.resolve(response);
        });
    }

    delete(event) {
        this.service
            .deleteWellConfig(event.payload.id)
            .subscribe((response) => {
                event.resolve(response);
            });
    }

    assign(event) {
        this.service
            .assignSchematic(this.schematic.wellStringId, event.payload.id)
            .subscribe(async (response) => {
                this.schematic = await this.getSchematic(
                    this.wellStringId,
                    true
                );
                event.resolve(response);
            });
    }

    getAllConfig(event) {
        const params = {
            wellStringId: this.wellStringId,
            isApplication: this.isApplication ? 'true' : 'false',
        };
        const allConfigsParams = {
            PageSize: event.payload?.PageSize ?? '',
            Page: event.payload?.Page ?? '',
            TotalPage: event.payload?.totalPages ?? '',
            Search: event.payload?.Search ?? {},
        };
        forkJoin({
            selected: this.service.getWellSchematicByWellString(params),
            all: this.service.getAllConfig(allConfigsParams),
        }).subscribe(({ selected, all }) => {
            const configs = {
                selectedConfig: selected.data,
                allConfig: all.data,
                isApplication: this.isApplication,
                schematic: this.schematic,
            };
            event.resolve(configs);
        });
    }

    getCdft(event) {
        if (this.cdftSchematic) {
            return event.resolve(this.cdftSchematic);
        }

        this.service
            .getCdftSchematic(this.schematic.wellStringId ?? this.wellStringId)
            .subscribe((response) => {
                event.resolve(response.data);
            });
    }

    getConfigurationHistory(event) {
        this.service
            .getConfigurationHistory(this.schematic.wellStringId)
            .subscribe((response) => {
                event.resolve(response.data);
            });
    }

    submit(event) {
        const payload = {
            wellStringId: this.wellStringId,
            ...event.payload,
        };

        this.service
            .submitWellSchematicByString(payload)
            .subscribe((response) => {
                event.resolve(response);
            });
    }
}
